import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  // Timer states
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [isActive, setIsActive] = useState(false);
  const [isWorkSession, setIsWorkSession] = useState(true);
  const [sessionCount, setSessionCount] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  
  // Settings
  const [workDuration, setWorkDuration] = useState(25);
  const [breakDuration, setBreakDuration] = useState(5);
  
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  
  // Calculate progress for circular indicator
  const totalTime = (isWorkSession ? workDuration : breakDuration) * 60;
  const progress = ((totalTime - timeLeft) / totalTime) * 100;
  
  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Start/stop timer
  const toggleTimer = () => {
    setIsActive(!isActive);
  };
  
  // Reset timer
  const resetTimer = () => {
    setIsActive(false);
    setTimeLeft(isWorkSession ? workDuration * 60 : breakDuration * 60);
  };
  
  // Skip to next session
  const skipSession = () => {
    setIsActive(false);
    if (isWorkSession) {
      // Complete work session
      setSessionCount(prev => prev + 1);
      setIsWorkSession(false);
      setTimeLeft(breakDuration * 60);
    } else {
      // Complete break session
      setIsWorkSession(true);
      setTimeLeft(workDuration * 60);
    }
  };
  
  // Apply settings
  const applySettings = () => {
    setIsActive(false);
    setTimeLeft(isWorkSession ? workDuration * 60 : breakDuration * 60);
    setShowSettings(false);
  };
  
  // Timer logic
  useEffect(() => {
    if (isActive && timeLeft > 0) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (isActive && timeLeft === 0) {
      // Timer completed
      setIsActive(false);
      audioRef.current.play();
      
      if (isWorkSession) {
        // Work session completed, start break
        setSessionCount(prev => prev + 1);
        setIsWorkSession(false);
        setTimeLeft(breakDuration * 60);
      } else {
        // Break completed, start work
        setIsWorkSession(true);
        setTimeLeft(workDuration * 60);
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isActive, timeLeft, isWorkSession, workDuration, breakDuration]);
  
  // Update timer when settings change
  useEffect(() => {
    if (!isActive) {
      setTimeLeft(isWorkSession ? workDuration * 60 : breakDuration * 60);
    }
  }, [workDuration, breakDuration, isWorkSession, isActive]);
  
  return (
    <div className="App">
      <header className="App-header">
        <h1>Pomodoro Timer</h1>
        <p className="session-counter">Sessions Completed: {sessionCount}</p>
      </header>
      
      <main className="timer-container">
        <div className="progress-circle">
          <svg width="300" height="300" viewBox="0 0 300 300">
            {/* Background circle */}
            <circle
              cx="150"
              cy="150"
              r="130"
              stroke="#e0e0e0"
              strokeWidth="20"
              fill="none"
            />
            {/* Progress circle */}
            <circle
              cx="150"
              cy="150"
              r="130"
              stroke={isWorkSession ? "#4CAF50" : "#2196F3"}
              strokeWidth="20"
              fill="none"
              strokeLinecap="round"
              strokeDasharray="817"
              strokeDashoffset={817 - (817 * progress) / 100}
              transform="rotate(-90 150 150)"
            />
          </svg>
          
          <div className="timer-display">
            <div className="time">{formatTime(timeLeft)}</div>
            <div className="session-type">
              {isWorkSession ? 'Work Session' : 'Break Time'}
            </div>
          </div>
        </div>
        
        <div className="controls">
          <button onClick={toggleTimer} className="control-btn primary">
            {isActive ? 'Pause' : 'Start'}
          </button>
          <button onClick={resetTimer} className="control-btn secondary">
            Reset
          </button>
          <button onClick={skipSession} className="control-btn secondary">
            Skip
          </button>
          <button 
            onClick={() => setShowSettings(!showSettings)} 
            className="control-btn settings-btn"
          >
            ⚙️ Settings
          </button>
        </div>
        
        {showSettings && (
          <div className="settings-panel">
            <h3>Timer Settings</h3>
            <div className="setting">
              <label>Work Duration (minutes):</label>
              <input
                type="range"
                min="1"
                max="60"
                value={workDuration}
                onChange={(e) => setWorkDuration(parseInt(e.target.value))}
              />
              <span>{workDuration} min</span>
            </div>
            <div className="setting">
              <label>Break Duration (minutes):</label>
              <input
                type="range"
                min="1"
                max="30"
                value={breakDuration}
                onChange={(e) => setBreakDuration(parseInt(e.target.value))}
              />
              <span>{breakDuration} min</span>
            </div>
            <button onClick={applySettings} className="apply-btn">
              Apply Settings
            </button>
          </div>
        )}
      </main>
      
      <audio ref={audioRef} src="https://assets.mixkit.co/sfx/preview/mixkit-alarm-digital-clock-beep-989.mp3" />
      
      <footer className="App-footer">
        <p>Focus for {workDuration} minutes, break for {breakDuration} minutes</p>
      </footer>
    </div>
  );
}

export default App;
